import * as Yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { AttributeTypes } from "@iddataweb/idw-common-ui/enums";
import { Logger } from './logger';
import { AttributeFormState } from "../types/state/attribute-form-state";

const isRequired = (attribute: AttributeFormState) => {
    return !(attribute.isOptional || attribute.optional);
};

const getSchemaForAttributeType = (attribute: AttributeFormState): Yup.StringSchema<string, object, string, any, string> => {
    let YupFunc = Yup.string();

    if (attribute.templateName === AttributeTypes.CUSTOM) {
        if (isRequired(attribute)) {
            YupFunc = YupFunc.required("REQUIRED");
        }
        return YupFunc;
    }

    const parameter = attribute.type.includes("CustomAttribute")
        ? attribute.templateName
        : attribute.type;

    switch (parameter) {
        case AttributeTypes.FULL_NAME:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.fullName.required');
            }
            return YupFunc.max(50, 'validation.fullName.max');
        case AttributeTypes.COMPANY_NAME:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.companyName.required');
            }
            return YupFunc.min(2, 'validation.companyName.min').max(64, 'validation.companyName.max');
        case AttributeTypes.INTERNATIONAL_TELEPHONE:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.internationalTelephone.required');
            }
            return YupFunc.test(
                'validPhoneNumber',
                'validation.internationalTelephone.validPhoneNumber',
                (value: string) => {
                    // Check that we're not looking at a masked value
                    if (value === undefined || /##+/.test(value)) {
                        Logger.info('Using the masked value for phone number, validation is good');
                        return true;
                    }
                    const isValid = isValidPhoneNumber(value);
                    return isValid;
                }
            );
        case AttributeTypes.PIN_DELIVERY_PREFERENCE:
        case AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.pinDeliveryPreference.required');
            }
            return YupFunc;
        case AttributeTypes.INTERNATIONAL_ADDRESS:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.internationaladdress.required');
            }
            return YupFunc;
        case AttributeTypes.EMAIL:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.email.required');
            }
            return YupFunc.email('validation.email.email').max(64, 'validation.email.max');
        case AttributeTypes.SSN9:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.ssn9.required');
            }
            return YupFunc;
        case AttributeTypes.SSN4:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.ssn4.required');
            }
            return YupFunc;
        case AttributeTypes.DOB:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.dateOfBirth.required');
            }
            return YupFunc.min(10, 'validation.dateOfBirth.required');
        case AttributeTypes.NATIONAL_ID:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.nationalid.required');
            }
            return YupFunc;
        case AttributeTypes.DATE:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.date.required');
            }
            return YupFunc;
        case AttributeTypes.GENDER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.gender.required');
            }
            return YupFunc;
        case AttributeTypes.COUNTRY:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.country.required');
            }
            return YupFunc;
        case AttributeTypes.DRIVERS_LICENSE_NUMBER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.driverLicenseNumber.required');
            }
            return YupFunc;
        case AttributeTypes.ID_CARD_NUMBER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.idCardNumber.required');
            }
            return YupFunc;
        case AttributeTypes.PASSPORT_NUMBER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.passportNumber.required');
            }
            return YupFunc;
        case AttributeTypes.LICENSE_OR_ID_CARD:
        case AttributeTypes.LICENSE_OR_PASSPORT:
        case AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE:
        case AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD:
        case AttributeTypes.PASSPORT_OR_ID_CARD:
        case AttributeTypes.AADHAR_PAN_OR_PASSPORT:
        case AttributeTypes.INTERNATIONAL_LICENSE_OR_PASSPORT:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.idSelection.required');
            }
            return YupFunc;
        case AttributeTypes.AADHAR_CARD_NUMBER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.aadharCardNumber.required');
            }
            return YupFunc;
        case AttributeTypes.PAN_NUMBER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.panNumber.required');
            }
            return YupFunc;
        case AttributeTypes.PHYSICIAN_LICENSE_NUMBER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.physicianLicenseNumber.required');
            }
            return YupFunc;

        case AttributeTypes.DRIVERS_LICENSE_STATE:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.driversLicenseState.required');
            }
            return YupFunc;
        case AttributeTypes.POLICY_NUMBER:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.policyNumber.required');
            }
            return YupFunc.max(18, 'validation.policyNumber.max').test(
                'validAlphaNumeric',
                'validation.policyNumber.invalid',
                (value: string) => {
                    return /^[a-z0-9]+$/i.test(value);
                }
            );
        case AttributeTypes.ALPHANUMERIC:
        case AttributeTypes.ALPHANUMERIC_OTHER:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.alphanumeric.required');
            }
            return YupFunc.test('validAlphaNumeric', 'validation.alphanumeric.invalid', (value: string) => {
                return /^[a-z0-9]+$/i.test(value);
            });
        case AttributeTypes.NUMERIC:
            if (isRequired(attribute)) {
                YupFunc = YupFunc.required('validation.numeric.required');
            }
            return YupFunc.test('validNumeric', 'validation.numeric.invalid', (value: string) => {
                return /^\d+$/.test(value);
            });
        case AttributeTypes.BIO_GOV_OR_KBA:
            if (isRequired(attribute)) {
                return YupFunc.required('validation.bioGovOrKbaSelection.required');
            }
            return YupFunc;
        case AttributeTypes.NATIONALITY:
            if (isRequired(attribute)) {
                return YupFunc.required("validation.nationality.required");
            }
            return YupFunc;
        default:
            return YupFunc;
    }
};

const performValidation = (
    attributeFormState: AttributeFormState,
    attributeValue: string,
    path: string,
    createError: (params?: { path?: string | undefined; message?: string | undefined; params?: object | undefined; }) => Yup.ValidationError
) => {
    if (attributeFormState.isReadOnly) {
        return true;
    }

    const attributeSchema = getSchemaForAttributeType(attributeFormState);
    try {
        attributeSchema.validateSync(attributeValue, { abortEarly: true });
    } catch (validationError) {
        return createError({ path, message: validationError.message });
    }

    return true;
};

export const generateAttributeValidationSchema = (attributeFormStates: Array<AttributeFormState>) => {
    Yup.addMethod<Yup.StringSchema>(Yup.string, 'performValidation', function (attributeFormState: AttributeFormState) {
        return this.test('performValidation', 'Attribute validation error', function (attributeValue: string) {
            const { path, createError } = this;
            return performValidation(attributeFormState, attributeValue, path, createError);
        });
    });

    const attributeValuesShape: { [key: string]: Yup.StringSchema<string, object, string, any, string> } = {};
    attributeFormStates.forEach(afs => {
        attributeValuesShape[afs.type] = Yup.string().performValidation(afs);
    });

    const schema = Yup.object().shape(attributeValuesShape);

    return schema;
};